import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import Button from '../../../../components/Button';

import { inAppCancellation as events } from '../../../../lib/analytics/events';
import { useZendeskCancellationTicket } from '../hooks/useZendesk';
import CancelConfirmationModal from '../../components/CancelConfirmationModal';
import heartIconImg from '../../../../assets/images/in_app_cancellation/heart-yellow-bg.png';
import kitIconImg from '../../../../assets/images/in_app_cancellation/kit-yellow-bg.png';
import { useLocation } from 'react-router-dom';
import * as gqlTypes from '../../../../types/gql-op-types';
import AppPaths from '../../../../AppPaths';
import makersS3Image from '../../../../assets/images/in_app_cancellation/s3_makers_flat.png';
import classNames from 'classnames';
import { BaseInAppCancellationState } from '../util/types';
import ErrorScreen from '../../components/ErrorScreen/ErrorScreen';

export interface MeetMakersState extends BaseInAppCancellationState {
  selectedCancelReason: gqlTypes.zendeskCancellationReason;
}

export default function MeetMakers() {
  const { t } = useTranslation();
  const { device } = usePurchaseSubscriptionContext();
  const location = useLocation<MeetMakersState>();
  const { selectedCancelReason, subscriptionCategory } = location.state;
  const petName = device.pet!.name;
  events.pageLoad({ petId: device.pet!.id, subscriptionCategory }, 'Discover Fi Makers');

  // Sometimes, this page loads halfway down the screen. This should mitigate those instances.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { mutation, loading } = useZendeskCancellationTicket({
    analyticsEvent: events.cancelFromContactPage,
    selectedCancelReason,
    device,
    subscriptionCategory,
  });

  if (loading) {
    return loading;
  }

  if (!device.subscription?.id) {
    return <ErrorScreen />;
  }

  const handleCancel = () => {
    events.meetMakersContinue({ petId: device.pet?.id, subscriptionCategory });
    if (!device.subscription?.id) {
      throw new Error('Something went wrong.');
    }
    mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription.id,
          cancellationReason: selectedCancelReason,
          additionalText: undefined,
        },
      },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.meetMakersBack({ petId: device.pet?.id, subscriptionCategory })}
          closeButtonAnalyticsEvent={() => events.meetMakersClose({ petId: device.pet?.id, subscriptionCategory })}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.largeHeader}>
            <h1>{t('meet_our_premium_maker_editions')}</h1>
          </div>
          <div className={styles.featureDescription}>
            <img src={heartIconImg} alt={t('heart_icon')} className={styles.icon} />
            <p>{t('we_partner_with_a_community_of_independent_creators_to_offer_more_styles_and_variety')}</p>
          </div>
          <div className={styles.featureDescription}>
            <img src={kitIconImg} alt={t('kit_icon')} className={styles.icon} />
            <p>{t('select_for_comfort_material_and_swagger_to_give_pet_name_the_best_fit_possible', { petName })}</p>
          </div>
        </div>

        <img
          className={classNames(styles.collarAvatar, styles.makersCollarAvatar)}
          src={makersS3Image}
          alt={t('makers_collar_flat')}
        />
        <div className={styles.buttonContainer}>
          <Button
            className={styles.button}
            onClick={() => {
              events.meetMakersExplore({ petId: device.pet?.id, subscriptionCategory });
              window.open(AppPaths.FiMakers, '_blank');
            }}
          >
            {t('explore_maker_editions')}
          </Button>
          <CancelConfirmationModal
            onContinue={() => handleCancel()}
            pageName={t('manage_membership_other_reason')}
            petId={device.pet!.id}
            trigger={
              <Button className={styles.button} tertiary={true}>
                {t('continue_with_cancellation')}
              </Button>
            }
          />
        </div>
      </div>
    </>
  );
}
